/* Enhancement only: no network requests, navigation interception or data math. */
(() => {
  if (window.BBMotion) return;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const touch = matchMedia('(hover: none), (pointer: coarse), (max-width: 749px)');
  const running = new Map();
  const seen = new WeakSet();
  const seenContent = new Set();
  const values = new WeakMap();
  const observed = new Set();
  const progressElements = new Set();
  const progressMutations = new MutationObserver(records => records.forEach(record => prepareProgress(record.target)));
  let tokens;
  const readTokens = () => {
    const css = getComputedStyle(document.documentElement);
    tokens = Object.fromEntries(['instant', 'micro', 'control', 'state', 'drawer', 'close', 'reveal', 'progress', 'stagger', 'distance', 'menu-stagger', 'menu-open', 'menu-close', 'bag', 'success', 'remove', 'badge']
      .map(key => {
        const value = css.getPropertyValue('--bb-motion-' + key).trim();
        // Shopify's CSS optimization can serialize 310ms as .31s. WAAPI
        // durations/delays always require milliseconds; distances remain px.
        const multiplier = key !== 'distance' && /(^|[\d.])s$/.test(value) ? 1000 : 1;
        return [key, (parseFloat(value) || 0) * multiplier];
      }));
    tokens.ease = css.getPropertyValue('--bb-motion-ease').trim() || 'ease-out';
  };
  const cancel = element => {
    running.get(element)?.cancel();
    running.delete(element);
  };
  const play = (element, frames, kind = 'micro', delay = 0) => {
    if (!element) return null;
    cancel(element);
    if (reduced.matches || !element.animate || !element.isConnected) return null;
    try {
      const animation = element.animate(frames, { duration: tokens[kind], delay, easing: tokens.ease, fill: 'backwards' });
      running.set(element, animation);
      animation.finished.then(() => {
        if (running.get(element) === animation) running.delete(element);
      }).catch(() => {});
      return animation;
    } catch { return null; }
  };
  const reveal = (element, delay = 0, distance = tokens.distance) => play(element,
    [{ opacity: 0, translate: '0 ' + distance + 'px' }, { opacity: 1, translate: '0 0' }], 'reveal', delay);
  const batch = (elements, limit = touch.matches ? 4 : 8) => {
    [...elements].slice(0, limit).forEach((element, index) => reveal(element, index * tokens.stagger));
  };
  const visible = element => {
    const box = element.getBoundingClientRect();
    return box.width > 0 && box.height > 0 && box.bottom > 0 && box.top < innerHeight;
  };
  const removeRows = async rows => {
    const animations = rows.filter(visible).map(row =>
      play(row, [{ opacity: 1, translate: '0 0' }, { opacity: 0, translate: '16px 0' }], 'remove')).filter(Boolean);
    if (!animations.length) return;
    let timeout;
    // Even a paused/failed animation cannot hold the confirmed cart render.
    try {
      await Promise.race([Promise.allSettled(animations.map(animation => animation.finished)),
        new Promise(resolve => { timeout = setTimeout(resolve, tokens.state + tokens.instant); })]);
    } finally { clearTimeout(timeout); animations.forEach(animation => animation.cancel()); }
  };
  const modules = '.bb-tees-card, .bb-product-side-module, .bb-info-panel, .bb-rewards-tier, .bb-rewards-earn__item, .bb-reward-card, .bbmh-status, .bbmh-metric, .bbmh-panel, .bb-rewards-activity__list article, .bb-rewards-leaderboard__row';
  const numbers = '[data-bb-cart-estimate-points], [data-bb-estimate-points], [data-bb-product-points], [data-bb-current-tier], [data-bb-tier-points], [data-bb-balance], [data-bb-next-tier], [data-cart-subtotal], .bb-cart-summary__rows strong, .cart-items__price';
  const progress = '[data-bb-progress-fill], .bbmh-progress i';
  const find = (root, selector) => [...(root.matches?.(selector) ? [root] : []), ...(root.querySelectorAll?.(selector) || [])];
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    let index = 0;
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      observer.unobserve(entry.target);
      observed.delete(entry.target);
      if (seen.has(entry.target)) return;
      if (entry.target.matches(progress)) {
        if (parseFloat(entry.target.style.width) > 0) {
          seen.add(entry.target);
          play(entry.target, [{ scale: '0 1' }, { scale: '1 1' }], 'progress');
        }
        return;
      }
      seen.add(entry.target);
      // All unanimated content stays visible. Never stagger a whole long grid.
      if (index < (touch.matches ? 4 : 8)) {
        const leaderboard = entry.target.matches('.bb-rewards-leaderboard__row');
        reveal(entry.target, index++ * (leaderboard ? (touch.matches ? 20 : 30) : tokens.stagger), leaderboard ? 8 : tokens.distance);
      }
    });
  }, { threshold: .08 }) : null;
  const prepareProgress = element => {
    // Only progress fills need style observation. Glass/reflection styles do not.
    if (!progressElements.has(element)) {
      progressElements.add(element);
      progressMutations.observe(element, { attributes: true, attributeFilter: ['style'] });
    }
    // Only enhance an actual positive fill supplied by the existing data code.
    // This handles both a later width update and markup inserted already loaded.
    if (seen.has(element) || observed.has(element) || !(parseFloat(element.style.width) > 0)) return;
    if (reduced.matches) { seen.add(element); return; }
    if (visible(element)) {
      seen.add(element);
      play(element, [{ scale: '0 1' }, { scale: '1 1' }], 'progress');
    } else if (observer) {
      observed.add(element);
      observer.observe(element);
    }
  };
  const changedNumber = element => {
    const next = element.textContent.trim();
    const previous = values.get(element);
    values.set(element, next);
    if (previous !== undefined && previous !== next && next && !next.includes('—') && visible(element)) {
      play(element, [{ opacity: .35, translate: '0 5px' }, { opacity: 1, translate: '0 0' }]);
    }
  };
  const init = (root = document) => {
    find(root, numbers).forEach(changedNumber);
    find(root, progress).forEach(prepareProgress);
    find(root, modules).forEach(element => {
      if (seen.has(element) || observed.has(element)) return;
      if (element.matches('[data-bb-earn-action], .bb-rewards-activity__list article, .bb-rewards-leaderboard__row, .bb-rewards-tier, .bb-reward-card')) {
        const key = element.className + ':' + element.textContent.trim();
        if (seenContent.has(key)) { seen.add(element); return; }
        seenContent.add(key);
      }
      if (reduced.matches || !observer) { seen.add(element); return; }
      observed.add(element);
      observer.observe(element);
    });
  };
  const cleanup = root => {
    for (const element of observed) {
      if (!element.isConnected || root?.contains(element)) { observer?.unobserve(element); observed.delete(element); }
    }
    for (const element of running.keys()) if (!element.isConnected || root?.contains(element)) cancel(element);
    let removedProgress = false;
    for (const element of progressElements) {
      if (!element.isConnected || root?.contains(element)) { progressElements.delete(element); removedProgress = true; }
    }
    if (removedProgress) {
      progressMutations.disconnect();
      progressElements.forEach(element => progressMutations.observe(element, { attributes: true, attributeFilter: ['style'] }));
    }
  };
  // A single scoped observer. Countdown ticks do not create animations.
  const mutations = new MutationObserver(records => {
    const changed = new Set();
    records.forEach(record => {
      const target = record.target instanceof Element ? record.target : record.target.parentElement;
      const scalar = target?.closest(numbers);
      if (scalar) changed.add(scalar);
      record.addedNodes.forEach(node => { if (node instanceof Element) init(node); });
      // Variant selection replaces the native gallery after its real response.
      // Crossfade changed desktop media only; leave touch swipe and size-only
      // selections untouched, and never delay the native replacement.
      const previousGallery = [...(record.removedNodes || [])].find(node => node instanceof Element && node.matches('media-gallery'));
      if (previousGallery && matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)').matches) {
        const nextGallery = [...record.addedNodes].find(node => node instanceof Element && node.matches('media-gallery'));
        const previousImage = previousGallery.querySelector('.product-media__image');
        const nextImage = nextGallery?.querySelector('.product-media__image');
        if (nextGallery?.dataset.bbVariantMedia && nextGallery.dataset.bbVariantMedia !== previousGallery.dataset.bbVariantMedia
          && nextImage && nextImage.getAttribute('src') !== previousImage?.getAttribute('src') && visible(nextImage)) {
          play(nextImage, [{ opacity: .25 }, { opacity: 1 }], 'state');
        }
      }
    });
    changed.forEach(changedNumber);
    cleanup();
  });
  const observeRoots = () => {
    mutations.disconnect();
    document.querySelectorAll('#MainContent, #shopify-section-cart-drawer-section').forEach(root =>
      mutations.observe(root, { subtree: true, childList: true, characterData: true }));
  };
  const start = () => {
    readTokens();
    init();
    observeRoots();
    // Do not animate the LCP artwork or a whole page.
    const hero = document.querySelector('bb-hero');
    if (hero) [...hero.querySelectorAll('.bb-hero__headline, .bb-hero__subheading, .bb-hero__copy .bb-glass-button, .bb-category-card')]
      .slice(0, touch.matches ? 4 : 7).forEach((element, index) => {
        const distance = element.matches('.bb-hero__headline') ? (touch.matches ? 12 : 16)
          : element.matches('.bb-hero__subheading') ? (touch.matches ? 8 : 12)
          : element.matches('.bb-glass-button') ? (touch.matches ? 8 : 10) : (touch.matches ? 10 : 14);
        reveal(element, index * (touch.matches ? 28 : 40), distance);
      });
  };
  document.addEventListener('theme-drawer:open', event => {
    if (event.target.id !== 'cart-drawer') return;
    // The cart controller owns each product's entrance. A second row fade
    // would compete with it and restart on interruptions.
    if (event.target.querySelector('dialog[data-sm-bag-motion]')) return;
    [...event.target.querySelectorAll('.cart-items__table-row')].filter(row => row.querySelector('.cart-items__title') && visible(row))
      .slice(0, touch.matches ? 3 : 5).forEach((row, index) => play(row,
        [{ opacity: 0, translate: '0 ' + (touch.matches ? 10 : 14) + 'px' }, { opacity: 1, translate: '0 0' }],
        'bag', index * (touch.matches ? 28 : 40)));
  });
  document.addEventListener('slideshow:select', event => {
    if (!matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)').matches || !event.target.closest('.product-information') || event.detail?.trigger === 'drag' || event.detail?.trigger === 'scroll') return;
    play(event.detail?.slide?.querySelector('.product-media__image'), [{ opacity: .25 }, { opacity: 1 }], 'state');
  });
  document.addEventListener('shopify:cart:lines-update', event => {
    if (event.action !== 'update') return;
    event.promise?.then(({ detail }) => {
      if (detail?.didError) return;
      requestAnimationFrame(() => {
        const keys = new Set(event.lines?.map(line => line.id) || []);
        document.querySelectorAll('#cart-drawer .cart-items__table-row').forEach(row => {
          if (!keys.has(row.dataset.key)) return;
          play(row.querySelector('input[name="updates[]"], input[type="number"]'),
            [{ opacity: .35, translate: '0 6px' }, { opacity: 1, translate: '0 0' }]);
        });
      });
    }).catch(() => {});
  });
  document.addEventListener('shopify:section:load', event => { init(event.target); observeRoots(); });
  document.addEventListener('shopify:section:unload', event => cleanup(event.target));
  window.addEventListener('pagehide', () => { observer?.disconnect(); observed.clear(); mutations.disconnect(); cleanup(document); });
  window.addEventListener('pageshow', event => { if (event.persisted) { readTokens(); init(); observeRoots(); } });
  const preferenceChanged = () => {
    readTokens();
    for (const element of running.keys()) cancel(element);
    if (reduced.matches) { observed.forEach(element => seen.add(element)); observer?.disconnect(); observed.clear(); }
  };
  reduced.addEventListener('change', preferenceChanged);
  touch.addEventListener('change', preferenceChanged);
  window.BBMotion = { play, reveal, batch, cancel, removeRows, get tokens() { return tokens; }, get reduced() { return reduced.matches; } };
  start();
})();
